import React, { useState, useEffect, useMemo } from 'react'
import PastBookDetail from './PastBookDetail'
import { formatDate } from '../utils/dateFormat'

interface Book {
  id: number
  title: string
  author: string
  isbn?: string
  description?: string
  cover_url?: string
  genre?: string
  first_publish_year?: number
  number_of_pages?: number
}

interface BookPick {
  id: number
  club_id?: number
  club_name: string
  status: string
  started_at?: string
  completed_at?: string
  book: Book
  picked_by?: {
    id: number
    name: string
    email: string
  }
  season?: {
    id: number
    season_number: number
  }
  average_rating?: number
  review_count: number
  user_review?: {
    id: number
    rating: number
    review_text?: string
  }
}

interface MyPastReadsListProps {
  onBack: () => void
}

type SortField = 'title' | 'author' | 'genre' | 'club' | 'season' | 'rating' | 'completed_at'
type SortDirection = 'asc' | 'desc'

export default function MyPastReadsList({ onBack }: MyPastReadsListProps) {
  const [books, setBooks] = useState<BookPick[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [genreFilter, setGenreFilter] = useState<string>('all')
  const [clubFilter, setClubFilter] = useState<string>('all')
  const [sortField, setSortField] = useState<SortField>('completed_at')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')
  const [selectedBook, setSelectedBook] = useState<BookPick | null>(null)
  const [showDetail, setShowDetail] = useState(false)

  useEffect(() => {
    fetchBooks()
  }, [])

  const fetchBooks = async () => {
    try {
      const response = await fetch('/api/v1/my_book_picks')
      if (response.ok) {
        const data = await response.json()
        setBooks(data.book_picks)
      }
    } catch (err) {
      console.error('Failed to fetch books:', err)
    } finally {
      setLoading(false)
    }
  }

  // Get unique genres and clubs for filters
  const genres = useMemo(() => {
    const uniqueGenres = new Set(books.map(b => b.book.genre).filter(Boolean))
    return Array.from(uniqueGenres).sort()
  }, [books])

  const clubs = useMemo(() => {
    const uniqueClubs = new Set(books.map(b => b.club_name))
    return Array.from(uniqueClubs).sort()
  }, [books])

  // Calculate stats
  const stats = useMemo(() => {
    const totalBooks = books.length
    // Use user_review rating if available, otherwise use average_rating
    const ratingsOnly = books
      .map(b => {
        const rating = b.user_review?.rating || b.average_rating
        // Convert string ratings to numbers
        const numRating = typeof rating === 'string' ? parseFloat(rating) : rating
        return typeof numRating === 'number' && !isNaN(numRating) ? numRating : null
      })
      .filter((rating): rating is number => rating !== null && rating > 0)

    const avgRating = ratingsOnly.length > 0
      ? (ratingsOnly.reduce((sum, r) => sum + r, 0) / ratingsOnly.length).toFixed(1)
      : null

    const genreCounts = books.reduce((acc, book) => {
      if (book.book.genre) {
        acc[book.book.genre] = (acc[book.book.genre] || 0) + 1
      }
      return acc
    }, {} as Record<string, number>)

    const favoriteGenre = Object.entries(genreCounts).sort((a, b) => b[1] - a[1])[0]?.[0]

    return { totalBooks, avgRating, favoriteGenre }
  }, [books])

  // Filter and sort books
  const filteredAndSortedBooks = useMemo(() => {
    let filtered = books

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(pick =>
        pick.book.title.toLowerCase().includes(query) ||
        pick.book.author.toLowerCase().includes(query)
      )
    }

    // Genre filter
    if (genreFilter !== 'all') {
      filtered = filtered.filter(pick => pick.book.genre === genreFilter)
    }

    // Club filter
    if (clubFilter !== 'all') {
      filtered = filtered.filter(pick => pick.club_name === clubFilter)
    }

    // Sort
    const sorted = [...filtered].sort((a, b) => {
      let aVal: any
      let bVal: any

      switch (sortField) {
        case 'title':
          aVal = a.book.title.toLowerCase()
          bVal = b.book.title.toLowerCase()
          break
        case 'author':
          aVal = a.book.author.toLowerCase()
          bVal = b.book.author.toLowerCase()
          break
        case 'genre':
          aVal = a.book.genre?.toLowerCase() || ''
          bVal = b.book.genre?.toLowerCase() || ''
          break
        case 'club':
          aVal = a.club_name.toLowerCase()
          bVal = b.club_name.toLowerCase()
          break
        case 'season':
          aVal = a.season?.season_number || 0
          bVal = b.season?.season_number || 0
          break
        case 'rating':
          const aRating = a.user_review?.rating || a.average_rating
          const bRating = b.user_review?.rating || b.average_rating
          aVal = typeof aRating === 'string' ? parseFloat(aRating) : (aRating || 0)
          bVal = typeof bRating === 'string' ? parseFloat(bRating) : (bRating || 0)
          break
        case 'completed_at':
          aVal = a.completed_at ? new Date(a.completed_at).getTime() : 0
          bVal = b.completed_at ? new Date(b.completed_at).getTime() : 0
          break
        default:
          return 0
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1
      return 0
    })

    return sorted
  }, [books, searchQuery, genreFilter, clubFilter, sortField, sortDirection])

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return (
        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      )
    }
    return sortDirection === 'asc' ? (
      <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    ) : (
      <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    )
  }

  const activeFiltersCount = [
    searchQuery !== '',
    genreFilter !== 'all',
    clubFilter !== 'all'
  ].filter(Boolean).length

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-4 mb-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">My Past Reads</h1>
              <p className="text-gray-600 mt-1">Books you've picked across all clubs</p>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-4 border border-indigo-100">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-white rounded-lg shadow-sm">
                  <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Total Books</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.totalBooks}</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-xl p-4 border border-yellow-100">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-white rounded-lg shadow-sm">
                  <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Average Rating</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {stats.avgRating && !isNaN(Number(stats.avgRating)) ? stats.avgRating : 'N/A'}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 border border-green-100">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-white rounded-lg shadow-sm">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Favorite Genre</p>
                  <p className="text-2xl font-bold text-gray-900 truncate">{stats.favoriteGenre || 'N/A'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Filters and Search */}
          <div className="flex flex-col md:flex-row gap-3">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search by title or author..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
              />
            </div>
            <select
              value={genreFilter}
              onChange={(e) => setGenreFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
            >
              <option value="all">All Genres</option>
              {genres.map(genre => (
                <option key={genre} value={genre}>{genre}</option>
              ))}
            </select>
            <select
              value={clubFilter}
              onChange={(e) => setClubFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
            >
              <option value="all">All Clubs</option>
              {clubs.map(club => (
                <option key={club} value={club}>{club}</option>
              ))}
            </select>
          </div>

          {/* Active Filters */}
          {activeFiltersCount > 0 && (
            <div className="flex items-center gap-2 mt-3">
              <span className="text-sm text-gray-600">Active filters:</span>
              {searchQuery && (
                <span className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded text-xs">
                  Search: "{searchQuery}"
                </span>
              )}
              {genreFilter !== 'all' && (
                <span className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded text-xs">
                  Genre: {genreFilter}
                </span>
              )}
              {clubFilter !== 'all' && (
                <span className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded text-xs">
                  Club: {clubFilter}
                </span>
              )}
              <button
                onClick={() => {
                  setSearchQuery('')
                  setGenreFilter('all')
                  setClubFilter('all')
                }}
                className="text-xs text-indigo-600 hover:text-indigo-700 font-medium ml-2"
              >
                Clear all
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left">
                    <button
                      onClick={() => handleSort('title')}
                      className="flex items-center gap-2 text-xs font-semibold text-gray-700 uppercase tracking-wider hover:text-indigo-600"
                    >
                      Book
                      <SortIcon field="title" />
                    </button>
                  </th>
                  <th className="px-6 py-3 text-left">
                    <button
                      onClick={() => handleSort('club')}
                      className="flex items-center gap-2 text-xs font-semibold text-gray-700 uppercase tracking-wider hover:text-indigo-600"
                    >
                      Club
                      <SortIcon field="club" />
                    </button>
                  </th>
                  <th className="px-6 py-3 text-left">
                    <button
                      onClick={() => handleSort('genre')}
                      className="flex items-center gap-2 text-xs font-semibold text-gray-700 uppercase tracking-wider hover:text-indigo-600"
                    >
                      Genre
                      <SortIcon field="genre" />
                    </button>
                  </th>
                  <th className="px-6 py-3 text-left">
                    <button
                      onClick={() => handleSort('season')}
                      className="flex items-center gap-2 text-xs font-semibold text-gray-700 uppercase tracking-wider hover:text-indigo-600"
                    >
                      Season
                      <SortIcon field="season" />
                    </button>
                  </th>
                  <th className="px-6 py-3 text-left">
                    <button
                      onClick={() => handleSort('rating')}
                      className="flex items-center gap-2 text-xs font-semibold text-gray-700 uppercase tracking-wider hover:text-indigo-600"
                    >
                      Rating
                      <SortIcon field="rating" />
                    </button>
                  </th>
                  <th className="px-6 py-3 text-left">
                    <button
                      onClick={() => handleSort('completed_at')}
                      className="flex items-center gap-2 text-xs font-semibold text-gray-700 uppercase tracking-wider hover:text-indigo-600"
                    >
                      Completed
                      <SortIcon field="completed_at" />
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredAndSortedBooks.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center">
                      <div className="text-gray-500">
                        {activeFiltersCount > 0 ? 'No books match your filters' : 'No books yet'}
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredAndSortedBooks.map((pick) => (
                    <tr
                      key={pick.id}
                      onClick={() => {
                        setSelectedBook(pick)
                        setShowDetail(true)
                      }}
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          {pick.book.cover_url ? (
                            <img
                              src={pick.book.cover_url}
                              alt={pick.book.title}
                              className="w-10 h-14 object-cover rounded shadow-sm"
                            />
                          ) : (
                            <div className="w-10 h-14 bg-gradient-to-br from-indigo-100 to-purple-100 rounded flex items-center justify-center">
                              <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                              </svg>
                            </div>
                          )}
                          <div>
                            <p className="font-semibold text-gray-900">{pick.book.title}</p>
                            <p className="text-sm text-gray-600">{pick.book.author}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-900">{pick.club_name}</span>
                      </td>
                      <td className="px-6 py-4">
                        {pick.book.genre ? (
                          <span className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded text-xs font-medium">
                            {pick.book.genre}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {pick.season ? (
                          <span className="text-sm text-gray-900">Season {pick.season.season_number}</span>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {(() => {
                          const rating = pick.user_review?.rating || pick.average_rating
                          const numRating = typeof rating === 'string' ? parseFloat(rating) : rating
                          return (numRating && numRating > 0) ? (
                            <div className="flex items-center gap-1">
                              <svg className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                              </svg>
                              <span className="text-sm font-medium text-gray-900">{numRating}</span>
                              {pick.review_count > 1 && (
                                <span className="text-xs text-gray-500">({pick.review_count})</span>
                              )}
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )
                        })()}
                      </td>
                      <td className="px-6 py-4">
                        {pick.completed_at ? (
                          <span className="text-sm text-gray-900">
                            {formatDate(pick.completed_at)}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Results counter */}
          {filteredAndSortedBooks.length > 0 && (
            <div className="px-6 py-3 bg-gray-50 border-t border-gray-200">
              <p className="text-sm text-gray-600">
                Showing {filteredAndSortedBooks.length} of {books.length} books
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Book Detail Modal */}
      {showDetail && selectedBook && (
        <PastBookDetail
          bookPick={selectedBook}
          clubId={selectedBook.club_id}
          onClose={() => {
            setShowDetail(false)
            setSelectedBook(null)
          }}
          onDelete={() => {
            fetchBooks()
          }}
        />
      )}
    </div>
  )
}
